import cn from "classnames";
import BookShow from "components/shared/book/show";
import { Checkbox, Field } from "components/shared/form";
import { extendedPropertyFlags, extendedPropertyFlagsEnum, organizationRegistrationStatusesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import policy from "policies";
import React from "react";
import { Link } from "react-router-dom";
import { getOrganizationProperties } from "sources/extendedProperties";
import OrganizationCatalogItem from "./catalogItem";
import { config, defaultDaysBeforeBlockForNotification, initialState } from "./config";
import DemoPeriodForm from "./demoPeriodForm";
import Markup from "./markup";
import NormalizeCarNumbersForm from "./normalizeCarNumbersForm";
import OrganizationPropertyItem from "./propertyItem";
import Relay from "./relay";
import RelayIds from "./relayIds";

const normalizeBusinessPartner = (businessPartner) => {
    const normalizedBusinessPartner = businessPartner || {};
    return {
        ...normalizedBusinessPartner,
        daysBeforeBlockForNotification:
            normalizedBusinessPartner.daysBeforeBlockForNotification ?? defaultDaysBeforeBlockForNotification,
    };
};

const normalizeOrganizationSettings = (data) => ({
    ...data,
    businessPartner: normalizeBusinessPartner(data.businessPartner),
});

class ShowAdminOrganization extends BookShow {
    get dropdActions() {
        return [];
    }

    getRequestData = (data) => {
        const { carNumberNormalization, ...requestData } = data;
        return requestData;
    };

    getItem = (props) => {
        const { source, match } = props || this.props;
        const defParams = { isLoading: false, isValid: true, shouldUpdate: false };
        Promise.all([source.getItem(match.params), getOrganizationProperties(parseInt(match.params.id))]).then(
            ([data, properties]) => {
                const normalizedData = normalizeOrganizationSettings(data);
                return this.setState({ data: normalizedData, initial: normalizedData, properties, ...defParams });
            },
        );
    };

    renderFlagToggler = (propName) => {
        const { settings } = this.state.data;
        const cname = cn("checkbox", { "checkbox--checked": settings[propName] });
        return (
            <div className="py-1">
                <span className={cname} />
            </div>
        );
    };

    renderRelayIds = () => {
        return <RelayIds values={this.state.data.relayIds ?? []} setValue={this.setValue} />;
    };

    renderRelay = () => {
        if (this.state.data.registrationStatus === null) {
            return null;
        }

        return (
            <Relay
                value={this.state.data.relay ?? {}}
                setValue={this.setValue}
                renderField={this.renderField}
                renderCheckbox={this.renderCheckbox}
                registrationStatus={this.state.data.registrationStatus}
                getItem={this.getItem}
                id={this.props.match.params.id}
            />
        );
    };

    renderCatalogs = () => {
        const { catalogs, entityOptions } = this.state.data;
        return (
            <table className="table table-sm table-striped">
                <thead>
                    <tr>
                        <th>ID справочника</th>
                        <th>Название</th>
                        <th>Тип объекта</th>
                        <th>Показывать в мобильном приложении</th>
                        <th>Отображать в порожних заявках</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {catalogs.map((item) => {
                        const filteredEntityOptions = entityOptions
                            ? entityOptions.filter((v) => v.referencedCatalogId === item.id)
                            : [];
                        return (
                            <OrganizationCatalogItem
                                key={item.id}
                                {...item}
                                entityOptions={filteredEntityOptions}
                                mergeParams={this.props.mergeParams}
                                getItem={this.getItem}
                            />
                        );
                    })}
                </tbody>
            </table>
        );
    };

    renderExtendedProperties = () => {
        const { properties } = this.state;
        const { roles } = this.props;
        return (
            <table className="table table-sm table-striped">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Название</th>
                        <th>Описание</th>
                        <th>Тип объекта</th>
                        <th>PropertyName</th>
                        <th>TypeCode</th>
                        <th>Значение по умолчанию</th>
                        <th>{enumLabel(extendedPropertyFlags, extendedPropertyFlagsEnum.Invisible)}</th>
                        <th>Обязательное поле</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {properties.map((item) => (
                        <OrganizationPropertyItem
                            key={item.id}
                            {...item}
                            getItem={this.getItem}
                            modal={this.props.modal}
                        />
                    ))}
                </tbody>
                {policy("user", { user: { roles } }).isAdminWithWriteAccess() && (
                    <tfoot>
                        <tr>
                            <td colSpan="10">
                                <div className="inline-btn inline-btn--plus" onClick={this.createPropertyItem}>
                                    Создать
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
        );
    };

    createPropertyItem = () => {
        return this.props.modal({
            name: "extendedPropertyForm",
            title: "Создание",
            data: { organizationId: Number(this.props.match.params.id) },
            windowMod: "wide",
            callback: () => this.getItem(),
        });
    };

    showDemoPeriodModal = () => {
        this.props.modal({
            name: "default",
            title: "Демо период",
            windowMod: "sm",
            CustomBody: DemoPeriodForm,
            bodyData: { organizationId: Number(this.props.match.params.id) },
        });
    };

    showEditFieldOrderModal = () => {
        this.props.modal({
            name: "bidFieldsOrderForm",
            title: "Изменить порядок полей на форме",
            windowMod: "wide",
            organizationId: Number(this.props.match.params.id),
        });
    };

    showNormalizeNumbersModal = () => {
        this.props.modal({
            name: "default",
            title: "Нормализация госномеров ТС",
            windowMod: "sm",
            CustomBody: NormalizeCarNumbersForm,
            bodyData: { organizationId: Number(this.props.match.params.id) },
        });
    };

    renderNormalizeCarNumbers = () => {
        const { roles } = this.props;
        const disabled = !policy("user", { user: { roles } }).isAdminWithWriteAccess();
        if (disabled) {
            return null;
        }

        return (
            <button
                type="button"
                className="btn btn-sm btn-outline-primary mb-3"
                onClick={this.showNormalizeNumbersModal}
            >
                Нормализовать текущие номера
            </button>
        );
    };

    renderButton = () => {
        const { roles } = this.props;
        const cname = cn("btn btn-brand btn-sm", { disabled: !this.state.shouldUpdate });
        return (
            <div className="d-flex justify-content-between">
                <div>
                    <Link
                        className="btn btn-sm btn-outline-primary mr-2"
                        to={paths.adminOrganizationOdometerHistory(this.state.data.organization.id)}
                    >
                        История редактирования одометра
                    </Link>
                    <Link
                        className="btn btn-sm btn-outline-primary mr-2"
                        to={paths.adminOrganizationModules(this.state.data.organization.id)}
                    >
                        Доступные модули компании
                    </Link>
                    {this.state.data.registrationStatus === organizationRegistrationStatusesEnum.Completed && (
                        <div className="btn btn-sm btn-outline-primary mr-2" onClick={this.showDemoPeriodModal}>
                            Демо период
                        </div>
                    )}
                </div>
                {policy("user", { user: { roles } }).isAdminWithWriteAccess() && (
                    <div className={cname} onClick={this.apply}>
                        Обновить
                    </div>
                )}
            </div>
        );
    };

    renderCheckbox = ({ inputProps, ...props }) => {
        const { roles } = this.props;
        if (inputProps.disabled) {
            delete inputProps.disabled;
        }
        return (
            <Checkbox
                fieldBeforeLabel
                key={props.label}
                labelWrapper="col-auto col-form-label col-form-label-sm pl-0"
                fieldWrapper="col-auto"
                wrapper="panel__row"
                bWrapper="row align-items-center"
                onChange={this.setValue}
                value={this.state.data[inputProps.name]}
                inputProps={inputProps}
                disabled={!policy("user", { user: { roles } }).isAdminWithWriteAccess()}
                {...props}
            />
        );
    };

    renderField = ({ inputProps, ...props }) => {
        const { roles } = this.props;
        if (inputProps.disabled) {
            delete inputProps.disabled;
        }
        return (
            <Field
                key={props.label}
                wrapper="panel__row"
                onChange={this.setValue}
                value={this.state.data[inputProps.name]}
                inputProps={inputProps}
                disabled={!policy("user", { user: { roles } }).isAdminWithWriteAccess()}
                {...props}
            />
        );
    };

    renderCode = () => {
        return (
            <div className="row form-group field-wrap--value">
                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                    Пин-код
                </label>
                <div className="col-sm-8 col-md-9">
                    <label className="col-form-label col-form-label-sm">{this.state.data.code}</label>
                </div>
            </div>
        );
    };

    renderAtiSuExternalId = () => {
        const atiSuExternalId = this.state.data?.atiSuExternalId;
        if (!atiSuExternalId) {
            return null;
        }

        return (
            <div className="row form-group field-wrap--value">
                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                    ID компании ATI
                </label>
                <div className="col-sm-8 col-md-9">
                    <label className="col-form-label col-form-label-sm">{atiSuExternalId}</label>
                </div>
            </div>
        );
    };

    renderBody = () => {
        const { config, roles } = this.props;
        return (
            <>
                <h4 className="pb-3">{this.state.data.organization.name}</h4>
                <Markup
                    {...config}
                    renderCheckbox={this.renderCheckbox}
                    renderField={this.renderField}
                    renderSelect={this.renderSelect}
                    renderCatalogs={this.renderCatalogs}
                    renderRelayIds={this.renderRelayIds}
                    renderExtendedProperties={this.renderExtendedProperties}
                    renderCode={this.renderCode}
                    renderAtiSuExternalId={this.renderAtiSuExternalId}
                    renderRelay={this.renderRelay}
                    renderNormalizeCarNumbers={this.renderNormalizeCarNumbers}
                    setValue={this.setValue}
                    values={this.state.data}
                    changeFieldsOrderBtn={
                        policy("user", { user: { roles } }).isAdminWithWriteAccess() ? (
                            <div className="btn btn-sm btn-link" onClick={this.showEditFieldOrderModal}>
                                <i className="icon-uEAB4-arrows-up-down pr-1 sortable__button" />
                                Изменить порядок полей
                            </div>
                        ) : null
                    }
                    Button={this.renderButton}
                />
            </>
        );
    };
}

const ConfiguredShow = (props) => <ShowAdminOrganization {...props} config={config} initialState={initialState} />;

export default viewContainer(ConfiguredShow, "adminorganizations");
